"use client";

import { cn, variantBackgroundColors } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import parse from "html-react-parser";
import { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";

import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Link from "next/link";
import { SectionWrapper } from "../components/SectionWraper";

interface FeatureItem {
  icon?: string;
  title?: string;
  content?: string;
}

interface Feature4Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  items?: FeatureItem[];
  itemsWithDarkIconBg?: boolean;
  attributes?: React.HTMLProps<HTMLElement>;
}

/** Matches CarouselItem widths (Tailwind default breakpoints). */
function slidesPerViewForViewport(): number {
  if (typeof window === "undefined") {
    return 1;
  }
  if (window.matchMedia("(min-width: 1280px)").matches) {
    return 4;
  }
  if (window.matchMedia("(min-width: 1024px)").matches) {
    return 3;
  }
  if (window.matchMedia("(min-width: 768px)").matches) {
    return 2;
  }
  return 1;
}

const Feature4 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 4",
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  items,
  itemsWithDarkIconBg,
  attributes,
}: Feature4Props) => {
  const itemCount = items?.length ?? 0;
  const [api, setApi] = useState<CarouselApi>();
  const [progress, setProgress] = useState(0);
  const [firstVisible, setFirstVisible] = useState(1);
  const [lastVisible, setLastVisible] = useState(1);

  const syncProgress = useCallback(() => {
    if (!api || itemCount === 0) {
      return;
    }
    const perView = slidesPerViewForViewport();
    const snap = api.selectedScrollSnap();
    const first = snap + 1;
    const last = Math.min(snap + perView, itemCount);
    setFirstVisible(first);
    setLastVisible(last);
    setProgress((last / itemCount) * 100);
  }, [api, itemCount]);

  useEffect(() => {
    if (!api || itemCount === 0) {
      return;
    }
    syncProgress();
    api.on("reInit", syncProgress);
    api.on("select", syncProgress);
    return () => {
      api.off("reInit", syncProgress);
      api.off("select", syncProgress);
    };
  }, [api, itemCount, syncProgress]);

  useEffect(() => {
    if (typeof window === "undefined" || itemCount === 0) {
      return;
    }
    const mqs = [
      window.matchMedia("(min-width: 768px)"),
      window.matchMedia("(min-width: 1024px)"),
      window.matchMedia("(min-width: 1280px)"),
    ];
    const onChange = () => syncProgress();
    mqs.forEach((mq) => mq.addEventListener("change", onChange));
    return () => mqs.forEach((mq) => mq.removeEventListener("change", onChange));
  }, [itemCount, syncProgress]);

  const cardClass = cn(
    "px-0 border-none justify-between gap-0",
    variantBackgroundColors(darkMode, variant),
  );

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature4"
      attributes={attributes}
    >
      <div className="container">
        <div className="mb-10 flex flex-col items-center gap-6 md:mb-20" data-aos="fade-up">
          {badge && (
            <Badge variant="outline" bold>
              {badge}
            </Badge>
          )}
          {title && (
            <h2 className="text-center md:max-w-[500px] lg:max-w-[800px] md:mx-auto">
              {parse(title)}
            </h2>
          )}
          {content && (
            <div className="paragraph-lg text-muted-foreground text-center md:max-w-[65ch] md:mx-auto mb-4">
              {parse(content)}
            </div>
          )}
          {hasButton && (
            <div className="flex flex-col text-center gap-3">
              <Button asChild variant={buttonStyle} size="lg" className="w-full lg:w-auto">
                <Link href={buttonLink ?? ""}>
                  {buttonText}
                </Link>
              </Button>
              {microCopy && (
                <div className="paragraph-sm text-muted-foreground">
                  {microCopy}
                </div>
              )}
            </div>
          )}
        </div>
        <Carousel
          className="w-full"
          setApi={setApi}
          opts={{ align: "start", watchDrag: false }}
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <div className={cn("flex items-center justify-between gap-4 mb-6", items && items.length <= 4 && "xl:hidden")}>
            <div className="items-center gap-3 paragraph-sm text-muted-foreground flex flex-1 md:justify-end">
              <span>{itemCount ? String(firstVisible).padStart(2, "0") : "—"}</span>
              <Progress value={itemCount ? progress : 0} className="h-[2px] w-full max-w-[300px]" />
              <span>{itemCount ? `${lastVisible}/${itemCount}` : "—"}</span>
            </div>
            <div className="flex items-center gap-2">
              <CarouselPrevious className="static translate-y-0" grayBorder size="iconSm" />
              <CarouselNext className="static translate-y-0" grayBorder size="iconSm" />
            </div>
          </div>
          <CarouselContent>
            {items && items.length > 0 && items.map((item, index) => (
              <CarouselItem
                key={index}
                className="grid basis-full md:basis-1/2 lg:basis-1/3 xl:basis-1/4"
              >
                  <Card className={cardClass}>
                    <CardContent className="flex flex-col justify-center">
                    <div className="flex flex-col gap-4 md:gap-16">
                      {item.icon && (
                        <div className={cn("rounded-full p-3 size-12", itemsWithDarkIconBg ? "bg-gray-800" : "bg-background")}>
                          <span className="material-symbols-outlined text-brand">
                            {item.icon}
                          </span>
                        </div>
                      )}
                      <div className="flex flex-col gap-3">
                        {item.title && (
                          <h5 className="mt-4">
                            {item.title}
                          </h5>
                        )}
                        {item.content && (
                          <span className="paragraph-base text-muted-foreground">
                            {parse(item.content)}
                          </span>
                        )}
                      </div>
                    </div>
                    </CardContent>
                  </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </SectionWrapper>
  );
};

export { Feature4 };



